<template>
  <div>
    <div class="row d-flex align-items-center mb-2">
      <div class="col-4">
        <b-pagination v-model="page" :total-rows="totalCount" :per-page="perPage" class="mb-0" size="sm"/>
      </div>
      <span class="col-4 text-center">
        {{list.length}} van {{totalCount}}
      </span>
      <div class="col-4 text-right">
        <b-btn variant="link" @click="clearSearch"><span class="mdi mdi-filter-remove-outline"/> Reset filter</b-btn>
      </div>

    </div>
    <b-table v-bind="$attrs" v-on="$listeners" :fields="fields" :items="list" no-local-sorting @sort-changed="sortChanged" primary-key="id"
        :sort-by.sync="tableSortBy" :sort-desc.sync="tableSortDesc" :class="loading ? tableClass + ' busy' : tableClass" ref="tbl">
      <slot v-for="slot in Object.keys($slots)" :name="slot" :slot="slot"/>
      <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
        <slot :name="slot" v-bind="scope"/>
      </template>

      <template slot="top-row" v-if="hasSearchColumns">
        <b-td v-for="field in fields" :key="`search_${field.key}`" :class="field.tdClass">
          <template v-if="field.search === 'STRING'">
            <b-input v-model="search[field.key]" debounce="50" />
          </template>
          <template v-if="field.search === 'ENUM'">
            <table-wrap-enum-dropdown v-model="search[field.key]" :options="field.values"/>
          </template>
          <template v-if="field.search === 'BOOLEAN'">
            <table-wrap-bool-dropdown v-model="search[field.key]" />
          </template>
          <template v-if="field.selectAll">
            <b-checkbox @change="selectAllChange"></b-checkbox>
          </template>
        </b-td>
      </template>
    </b-table>
  </div>
  
</template>

<script>
import get           from 'lodash/get';
import isArray       from 'lodash/isArray';
import set           from 'lodash/set';

import gql_sort_name          from '@/lib/gql_sort_name';
import TableWrapEnumDropdown  from '@/components/TableWrapEnumDropdown';
import TableWrapBoolDropdown  from '@/components/TableWrapBoolDropdown';

export default
{
    apollo:
    {
        $subscribe:
        {
            items:
            {
                query() { return this.query },
                result({data})
                {
                    let f;

                    if(this.listPath)
                        f = get(data, this.listPath, '');
                    else
                        f = Object.values(data)[0];

                    if(Array.isArray(f))
                    {
                        this.list = Object.freeze(f);
                    }
                    else
                    {
                        this.list = Object.freeze(f.nodes);
                        this.totalCount = f.totalCount;
                    }

                    this.loading = false;
                },
                variables()
                {
                    let orderBy = [];
                    
                    if(isArray(this.orderBy))
                        orderBy = [...this.orderBy, 'PRIMARY_KEY_DESC'];
                    else
                        orderBy = [this.orderBy, 'PRIMARY_KEY_DESC'];

                    const newVars = {
                        ...this.variables,
                        filter: this.gqlFilter(),
                        first:  this.perPage,
                        offset: (this.page - 1) * this.perPage,
                        orderBy
                    }

                    const newVarsJson = JSON.stringify(newVars);
                    
                    this.loading = newVarsJson !== this.previousVars;
                    this.previousVars = newVarsJson;
                    
                    return newVars;
                }
            }
        }
    },
    beforeDestroy()
    {
        this.$emit(
            'update:state',
            {
                cache: this.list,
                cacheTotalCount: this.totalCount,
                orderBy: this.orderBy,
                page: this.page,
                tableSortBy: this.tableSortBy,
                tableSortDesc: this.tableSortDesc,
                routePath: this.initialPath,
                search: this.search
            }
        );
    },
    components: { TableWrapEnumDropdown, TableWrapBoolDropdown },
    data()
    {
        const
            defaultOrderBy        = get(this.resetState, 'orderBy', 'NATURAL'),
            defaultTableSortBy    = get(this.resetState, 'tableSortBy', ''),
            defaultTableSortDesc  = get(this.resetState, 'tableSortDesc', null),
            defaultSearch         = get(this.resetState, 'search', {});

        const
            orderBy               = get(this.state, 'orderBy', defaultOrderBy),
            tableSortBy           = get(this.state, 'tableSortBy', defaultTableSortBy),
            tableSortDesc         = get(this.state, 'tableSortDesc', defaultTableSortDesc),
            search                = get(this.state, 'search', defaultSearch),
            routePath             = get(this.state, 'routePath', null),
            initialPath           = this.$route.fullPath;

        const hasSearchColumns    = this.fields.some(f => f.search);

        let
            list = [],
            totalCount = 0,
            page = 1;

        if(routePath === initialPath)
        {
            list        = get(this.state, 'cache', list);
            totalCount  = get(this.state, 'cacheTotalCount', totalCount);
            page        = get(this.state, 'page', page);
        }

        return {
            page,
            list,
            search: this.initialSearch(search),
            totalCount,
            orderBy,
            tableSortBy,
            tableSortDesc,
            defaultOrderBy,
            defaultTableSortBy,
            defaultTableSortDesc,
            defaultSearch,
            loading: true,
            initialPath,
            hasSearchColumns
        }
    },
    methods:
    {
        clearSearch()
        {
            this.$vueSet(this, 'search', this.initialSearch(this.defaultSearch));
            this.orderBy        = this.defaultOrderBy;
            this.tableSortBy    = this.defaultTableSortBy;
            this.tableSortDesc  = this.defaultTableSortDesc;
        },
        clearSelected() { this.$refs.tbl.clearSelected() },
        initialSearch(existing = {})
        {
            const search = {};

            let field;

            for (field of this.fields)
            {
                switch(field.search)
                {
                    case 'STRING':
                        search[field.key] = get(existing, field.key, '');
                        break;
                    case 'ENUM':
                        search[field.key] = get(existing, field.key, field.values);
                        break;
                    case 'BOOLEAN':
                        search[field.key] = get(existing, field.key, undefined);
                        break;
                }
            }

            return search;
        },
        gqlFilter()
        {
            const search = {};

            let field, key, path;

            for (field of this.fields)
            {
                key = field.key;
                path = field.searchPath || key;

                switch (field.search)
                {
                    case 'STRING':
                        if(this.search[key] === '')
                            set(search, path, { });
                        else
                            set(search, path, { likeInsensitive: `%${this.search[key]}%` });
                        break;

                    case 'ENUM':
                        set(search, path, { in: this.search[key]});
                        break;

                    case 'BOOLEAN':
                        switch (this.search[key])
                        {
                            case true: set(search, path, {equalTo: true}); break;
                            case false: set(search, path, {equalTo: false}); break;
                            case undefined: set(search, path, undefined); break;
                        }
                        break;
                }
            }

            return search;
        },
        selectAllChange(ev)
        {
            if(ev)
                this.$refs.tbl.selectAllRows();
            else
                this.$refs.tbl.clearSelected();
        },
        sortChanged({sortBy, sortDesc})
        {
            if(sortBy === '')
            {
                // TODO: This happens when user clicks on unsortable column
            }
            else
            {
                const field   = this.fields.find(f => f.key === sortBy);
                this.orderBy  = gql_sort_name(field.sort, sortDesc);
            }
        },
        selectRow(idx) { this.$refs.tbl.selectRow(idx) }, // TODO: Handle selected when new table data loads
        unselectRow(idx) { this.$refs.tbl.unselectRow(idx) }
    },
    props:
    {
        query:      {},
        fields:     { type: Array, default() { return [] } },
        perPage:    { type: Number, required: false, default() { return 20; } },
        tableClass: { type: String, default() { return '' } },
        state:      { type: Object, default() { return {}} },
        resetState: { type: Object, default() { return {}} },
        listPath:   { type: String },
        variables:  { type: Object, default() {return {}}}
    }
}
</script>

<style>
table.busy tbody tr:not(.b-table-top-row)
{
    border-top: #ffc107 2px solid !important;
}
</style>